/**
 * A project on a phone or a tablet in portrait (below 1024px, hooks/useMediaQuery.js): one pane at a time. This bar,
 * on top of the project page, switches between the Chat and the Project (Dashboard, Files, Preview, History: the
 * Canvas, with its own top bar) and carries the notifications bell. Below 768px it leaves room on its left for the
 * fixed menu and search buttons (App.jsx), which open the FlowSidebar drawer and the global search.
 *
 * Without Chat (sandbox-first, chat off) there is nothing to switch: the bar shows only the bell, and the Project fills
 * the page.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ChatCircle, SquaresFour } from '@phosphor-icons/react';
import NotificationsBell from './collab/NotificationsBell';

const PANES = [
  { value: 'chat', label: 'Chat', Icon: ChatCircle },
  { value: 'project', label: 'Project', Icon: SquaresFour },
];

export default function CompactProjectBar({ pane, onPaneChange = null, onNavigate = null }) {
  return (
    <div className="compact-project-bar" data-testid="compact-project-bar">
      {onPaneChange ? (
        <div aria-label="Show" className="compact-pane-switch" role="group">
          {PANES.map(({ value, label, Icon }) => (
            <button
              aria-pressed={pane === value}
              className={`compact-pane-btn${pane === value ? ' is-on' : ''}`}
              data-testid={`compact-pane-${value}`}
              key={value}
              onClick={() => onPaneChange(value)}
              type="button"
            >
              <Icon aria-hidden size={18} weight={pane === value ? 'fill' : 'regular'} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      ) : <span className="compact-pane-spacer" />}
      {onNavigate && <NotificationsBell onNavigate={onNavigate} />}
    </div>
  );
}

CompactProjectBar.propTypes = {
  onNavigate: PropTypes.func,
  onPaneChange: PropTypes.func,
  pane: PropTypes.oneOf(['chat', 'project']).isRequired,
};
